// Single source of truth for room floor grid geometry and room-local <-> tile math.
// Pure TS: no PIXI, no React. Import constants and transforms from here only.

/** Floor diamond dimensions (from actual PNG: 1000×500). */
export const FLOOR_WIDTH = 1000;
export const FLOOR_HEIGHT = 500;

// Keep BASE_MAP_WIDTH_TILES as logical "old grid" (100×50). Increase
// GRID_SUBDIVISION for finer placement without rewriting math everywhere.
export const BASE_MAP_WIDTH_TILES = 10;
/** 1 => 100×50 tiles, 2 => 50×25, 4 => 25×12.5 */
export const GRID_SUBDIVISION = 4;
export const MAP_WIDTH_TILES = BASE_MAP_WIDTH_TILES * GRID_SUBDIVISION; // 40
export const TILE_WIDTH = FLOOR_WIDTH / MAP_WIDTH_TILES; // 25
export const TILE_HEIGHT = FLOOR_HEIGHT / MAP_WIDTH_TILES; // 12.5

export const HALF_TILE_WIDTH = TILE_WIDTH / 2;
export const HALF_TILE_HEIGHT = TILE_HEIGHT / 2;

/**
 * Continuous room-local (x, y) → isometric tile coords (no flooring).
 *   tileX = (x / halfTW + y / halfTH) / 2
 *   tileY = (y / halfTH − x / halfTW) / 2
 */
export function worldToTile(x: number, y: number): { tileX: number; tileY: number } {
  return {
    tileX: (x / HALF_TILE_WIDTH + y / HALF_TILE_HEIGHT) / 2,
    tileY: (y / HALF_TILE_HEIGHT - x / HALF_TILE_WIDTH) / 2,
  };
}

/**
 * Integer (or continuous) tile coords → room-local world point (tile lattice origin).
 *   x = (tileX − tileY) * halfTW
 *   y = (tileX + tileY) * halfTH
 */
export function tileToWorld(tileX: number, tileY: number): { x: number; y: number } {
  return {
    x: (tileX - tileY) * HALF_TILE_WIDTH,
    y: (tileX + tileY) * HALF_TILE_HEIGHT,
  };
}

/**
 * Room-local point → discrete tile indices (floor). Used by admin tile editor etc.
 * Extracted 1:1 from RoomCanvas admin tile-editor pointerToTile / former tileMath.localToTile.
 */
export function localToTile(local: { x: number; y: number }): { tileX: number; tileY: number } {
  const continuous = worldToTile(local.x, local.y);
  return {
    tileX: Math.floor(continuous.tileX),
    tileY: Math.floor(continuous.tileY),
  };
}

